import { memo, useRef, useEffect, type ComponentProps } from 'react';
import { DayPicker, getDefaultClassNames, type DayButton, type Locale } from 'react-day-picker';
import { cn } from '@new/lib';
import { Button } from '@new/common/ui/Button/Button';
import { ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';

const Calendar = ({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = 'label',
  buttonVariant: _buttonVariant = 'ghost',
  locale,
  formatters,
  components,
  weekStartsOn = 1,
  ...props
}: ComponentProps<typeof DayPicker> & {
  buttonVariant?: 'primary' | 'secondary' | 'ghost';
}) => {
  const defaultClassNames = getDefaultClassNames();

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn('p-2 bg-(--neutral-white-NW1000) rounded-lg!', className)}
      captionLayout={captionLayout}
      locale={locale}
      weekStartsOn={weekStartsOn}
      formatters={{
        formatMonthDropdown: (date) => date.toLocaleString(locale?.code, { month: 'short' }),
        ...formatters,
      }}
      classNames={{
        root: cn('w-fit', defaultClassNames.root),
        months: cn('relative flex flex-col gap-4 md:flex-row', defaultClassNames.months),
        month: cn('flex w-full flex-col gap-4', defaultClassNames.month),
        nav: cn(
          'absolute inset-x-0 top-0 flex w-full items-center justify-between gap-1',
          defaultClassNames.nav
        ),
        button_previous: cn(
          'size-9 p-0 select-none aria-disabled:opacity-50',
          defaultClassNames.button_previous
        ),
        button_next: cn(
          'size-9 p-0 select-none aria-disabled:opacity-50',
          defaultClassNames.button_next
        ),
        month_caption: cn(
          'flex h-9 w-full items-center justify-center px-9',
          defaultClassNames.month_caption
        ),
        dropdowns: cn(
          'flex h-9 w-full items-center justify-center gap-1.5 text-sm font-medium',
          defaultClassNames.dropdowns
        ),
        dropdown_root: cn('relative rounded-md!', defaultClassNames.dropdown_root),
        dropdown: cn('absolute inset-0 bg-white opacity-0', defaultClassNames.dropdown),
        caption_label: cn(
          'font-medium select-none',
          captionLayout === 'label'
            ? 'text-sm'
            : 'flex items-center gap-1 rounded-md! text-sm [&>svg]:size-3.5 [&>svg]:text-neutral-600',
          defaultClassNames.caption_label
        ),
        table: 'w-full border-collapse',
        weekdays: cn('flex', defaultClassNames.weekdays),
        weekday: cn(
          'flex-1 rounded-md! text-[0.8rem] font-normal text-neutral-600 select-none',
          defaultClassNames.weekday
        ),
        week: cn('mt-2 flex w-full', defaultClassNames.week),
        week_number_header: cn('w-9 select-none', defaultClassNames.week_number_header),
        week_number: cn(
          'text-[0.8rem] text-neutral-600 select-none',
          defaultClassNames.week_number
        ),
        day: cn(
          'group/day relative aspect-square h-full w-full p-0 text-center select-none',
          defaultClassNames.day
        ),
        range_start: cn(
          'relative isolate z-0 rounded-l-md! bg-neutral-100',
          defaultClassNames.range_start
        ),
        range_middle: cn('rounded-none!', defaultClassNames.range_middle),
        range_end: cn(
          'relative isolate z-0 rounded-r-md! bg-neutral-100',
          defaultClassNames.range_end
        ),
        today: cn('text-(--primary-O900)! font-bold!', defaultClassNames.today),
        outside: cn('text-neutral-400 aria-selected:text-neutral-400', defaultClassNames.outside),
        disabled: cn('text-neutral-400 opacity-50', defaultClassNames.disabled),
        hidden: cn('invisible', defaultClassNames.hidden),
        ...classNames,
      }}
      components={{
        Root: ({ className, rootRef, ...props }) => {
          return <div data-slot="calendar" ref={rootRef} className={cn(className)} {...props} />;
        },
        Chevron: ({ className, orientation, ...props }) => {
          if (orientation === 'left') {
            return <ChevronLeft className={cn('size-4 cursor-pointer', className)} {...props} />;
          }

          if (orientation === 'right') {
            return <ChevronRight className={cn('size-4 cursor-pointer', className)} {...props} />;
          }

          return <ChevronDown className={cn('size-4', className)} {...props} />;
        },
        DayButton: ({ ...props }) => <CalendarDayButton locale={locale} {...props} />,
        WeekNumber: ({ children, ...props }) => {
          return (
            <td {...props}>
              <div className="flex size-9 items-center justify-center text-center">{children}</div>
            </td>
          );
        },
        ...components,
      }}
      {...props}
    />
  );
};

const CalendarDayButton = memo(
  ({
    className,
    day,
    modifiers,
    locale,
    ...props
  }: ComponentProps<typeof DayButton> & { locale?: Partial<Locale> }) => {
    const defaultClassNames = getDefaultClassNames();

    const ref = useRef<HTMLButtonElement>(null);
    useEffect(() => {
      if (modifiers.focused) ref.current?.focus();
    }, [modifiers.focused]);

    return (
      <Button
        variant="ghost"
        size="sm"
        data-day={day.date.toLocaleDateString(locale?.code)}
        data-selected-single={
          modifiers.selected &&
          !modifiers.range_start &&
          !modifiers.range_end &&
          !modifiers.range_middle
        }
        data-range-start={modifiers.range_start}
        data-range-end={modifiers.range_end}
        data-range-middle={modifiers.range_middle}
        className={cn(
          'relative cursor-pointer rounded-full! isolate z-10 flex aspect-square size-auto w-full min-w-9 flex-col gap-1 border-0 leading-none font-normal data-[range-end=true]:rounded-full! data-[range-end=true]:bg-(--primary-O900)! data-[range-end=true]:text-white data-[range-middle=true]:rounded-full! data-[range-middle=true]:bg-neutral-100 data-[range-middle=true]:text-neutral-900 data-[range-start=true]:rounded-full! data-[range-start=true]:bg-(--primary-O900)! data-[range-start=true]:text-white! data-[selected-single=true]:bg-(--primary-O900)! data-[selected-single=true]:font-bold!',
          defaultClassNames.day,
          className
        )}
        {...props}
      />
    );
  }
);

CalendarDayButton.displayName = 'CalendarDayButton';

export { Calendar, CalendarDayButton };
